import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { computePeaks } from '../audio/analysis.js';
import { clamp, formatTime } from '../utils/misc.js';
import { Icon } from './Icon.js';

export interface WaveformProps {
  data?: Float32Array | null;
  duration: number;
  currentTime?: number;
  color?: string;
  height?: number;
  label?: string;
  interactive?: boolean;
  selectedRegion?: { start: number; end: number } | null;
  markers?: { id: string; time: number; label: string; color?: string }[];
  onSeek?: (time: number) => void;
  onSelectRegion?: (region: { start: number; end: number } | null) => void;
  compact?: boolean;
  showRuler?: boolean;
}

export function Waveform({
  data,
  duration,
  currentTime = 0,
  color = '#ff3b5c',
  height = 72,
  label,
  interactive = false,
  selectedRegion,
  markers = [],
  onSeek,
  onSelectRegion,
  compact = false,
  showRuler = false,
}: WaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(700);
  const [drag, setDrag] = useState<{ start: number; end: number } | null>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const resize = () => setWidth(Math.max(120, wrap.clientWidth));
    resize();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    ro?.observe(wrap);
    window.addEventListener('resize', resize);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, []);

  const peaks = useMemo(() => {
    if (!data?.length || duration <= 0) return null;
    const count = Math.max(128, Math.min(3000, Math.floor(width * 1.1)));
    return computePeaks(data, count);
  }, [data, width, duration]);

  const effectiveRegion = drag ?? selectedRegion;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const cssW = Math.max(1, width);
    const cssH = height;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    // Selection background
    if (effectiveRegion && duration > 0) {
      const x1 = (clamp(effectiveRegion.start, 0, duration) / duration) * cssW;
      const x2 = (clamp(effectiveRegion.end, 0, duration) / duration) * cssW;
      ctx.fillStyle = 'rgba(255,59,92,0.15)';
      ctx.fillRect(Math.min(x1, x2), 0, Math.abs(x2 - x1), cssH);
      ctx.strokeStyle = 'rgba(255,107,131,0.8)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(Math.min(x1, x2), 1, Math.abs(x2 - x1), cssH - 2);
      ctx.setLineDash([]);
    }

    // Quiet center line and waveform
    const center = cssH / 2;
    ctx.strokeStyle = 'rgba(148,163,184,0.13)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, center); ctx.lineTo(cssW, center); ctx.stroke();

    if (peaks) {
      const n = peaks.min.length;
      const step = cssW / n;
      ctx.lineCap = 'round';
      ctx.lineWidth = Math.max(1, Math.min(2.3, step * 0.6));
      for (let i = 0; i < n; i++) {
        const x = (i + 0.5) * step;
        const lo = peaks.min[i];
        const hi = peaks.max[i];
        const amp = Math.max(2, (hi - lo) * cssH * 0.39, Math.max(Math.abs(hi), Math.abs(lo)) * cssH * 0.38);
        const y1 = center - amp / 2;
        const y2 = center + amp / 2;
        ctx.globalAlpha = 0.9;
        ctx.strokeStyle = color;
        ctx.beginPath(); ctx.moveTo(x, y1); ctx.lineTo(x, y2); ctx.stroke();
      }
      ctx.globalAlpha = 1;
    } else {
      // Quiet but clear empty timeline, not a fake waveform.
      ctx.fillStyle = 'rgba(255,255,255,0.03)';
      for (let x = 10; x < cssW; x += 16) {
        ctx.fillRect(x, center - 1, 2, 2);
      }
    }

    // Markers
    for (const marker of markers) {
      if (!duration) continue;
      const x = (clamp(marker.time, 0, duration) / duration) * cssW;
      ctx.strokeStyle = marker.color ?? 'rgba(245,196,81,.8)';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, cssH); ctx.stroke();
      ctx.fillStyle = marker.color ?? '#f5c451';
      ctx.beginPath(); ctx.arc(x, 5, 3, 0, Math.PI * 2); ctx.fill();
    }

    // Playhead
    if (duration > 0) {
      const x = clamp(currentTime / duration, 0, 1) * cssW;
      ctx.strokeStyle = '#f8fafc';
      ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, cssH); ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.beginPath(); ctx.arc(x, 3, 3.5, 0, Math.PI * 2); ctx.fill();
    }
  }, [width, height, peaks, color, currentTime, duration, effectiveRegion, markers]);

  const timeFromX = useCallback((clientX: number) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect || duration <= 0) return 0;
    return clamp(((clientX - rect.left) / rect.width) * duration, 0, duration);
  }, [duration]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!interactive) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const t = timeFromX(e.clientX);
    setDrag({ start: t, end: t });
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!interactive || !drag) return;
    setDrag((d) => d ? { ...d, end: timeFromX(e.clientX) } : null);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (!interactive) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (drag) {
      const end = timeFromX(e.clientX);
      const start = Math.min(drag.start, end);
      const finish = Math.max(drag.start, end);
      if (finish - start >= 0.3) onSelectRegion?.({ start, end: finish });
      else {
        setDrag(null);
        onSelectRegion?.(null);
        onSeek?.(end);
      }
    } else {
      onSeek?.(timeFromX(e.clientX));
    }
    setDrag(null);
  };

  return (
    <div className="wave-wrap" ref={wrapRef}>
      {label && (
        <div className="wave-label-row">
          <span className="wave-label"><span className="wave-dot" style={{ background: color }} />{label}</span>
          <span className="wave-time">{formatTime(currentTime)} <span>/</span> {formatTime(duration)}</span>
        </div>
      )}
      <canvas
        ref={canvasRef}
        className={`wave-canvas ${interactive ? 'wave-interactive' : ''}`}
        style={{ height }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role={interactive ? 'slider' : undefined}
        aria-label={interactive ? 'Forma de onda, toque para posicionar ou arraste para selecionar um trecho' : label}
      />
      {showRuler && duration > 0 && (
        <div className="wave-ruler">
          {[0, .25, .5, .75, 1].map((f) => (
            <span key={f}>{formatTime(duration * f)}</span>
          ))}
        </div>
      )}
      {interactive && !compact && !selectedRegion && !drag && (
        <div className="wave-help"><Icon name="scissors" size={12} /> toque para ir · arraste para selecionar trecho</div>
      )}
      {effectiveRegion && (
        <div className="wave-selection-badge">
          <Icon name="scissors" size={12} /> {formatTime(Math.min(effectiveRegion.start, effectiveRegion.end))} — {formatTime(Math.max(effectiveRegion.start, effectiveRegion.end))}
        </div>
      )}
    </div>
  );
}
